import { isRejectedWithValue, Middleware, AnyAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import i18n from '../i18n'
import { errorText, isReadableServerText } from '../helpers/errorText'

/**
 * RTK Query middleware for global toasts: a failed request shows one error
 * toast, a successful mutation shows the server's confirmation when it is
 * readable in the page's language. The endpoints listed below are left to
 * their components.
 */

// Background widgets (banners, related listings, counters, price hints): a
// failure only hides the widget. Guests used to get error toasts from widgets
// they never asked for. The NBU exchange rate is a known-degraded dependency
// (prod egress to bank.gov.ua is blocked, infra backlog B3) with an editable
// default in the rental agreement.
const QUIET_QUERIES = new Set([
  'getExchangeRate',
  'getAllExchangeRates',
  'getNews',
  'getBanners',
  'getRelatedProperties',
  'getRecommendedProperties',
  'getPriceContext',
  'getPriceHint',
  'getPublicStats',
  'getUnreadCount',
  'getUnreadMessages',
  'getPushPublicKey',
  'getPropertyChanges',
  'getMyPropertyViewsDaily',
  'getUserProfileStats',
])

// Components that explain their own failure, inline under a field or with a
// specific toast: the global toast on top said the same thing twice.
const OWN_ERROR_UI = new Set([
  'register',
  'changePassword',
  'updateUserProfile',
  'sendChangeEmailCode',
  'verifyChangeEmailCode',
  'sendPhoneVerification',
  'verifyPhone',
  'connectTelegram',
  'disconnectTelegram',
  'enable2FA',
  'disable2FA',
  'verify2FACode',
  'send2FACode',
  'verifyWithDiiaBarcode',
  'revokeDiiaVerification',
  'deleteAccount',
  'updatePrivacy',
  'subscribePush',
  'unsubscribePush',
  'sendMessage',
  'uploadAvatar',
  'createBid',
])

// Components that confirm success themselves.
const OWN_SUCCESS_UI = new Set([
  'sendChatMessage',
  'submitInquiry',
  'promoteProperty',
  'createReview',
  'reportReview',
  'reportMessage',
  'forgotPassword',
  'resetPassword',
  'updateUserProfile',
  'sendAgreement',
  'subscribePush',
  'connectTelegram',
  'disconnectTelegram',
  'enable2FA',
  'disable2FA',
  'verify2FACode',
  'send2FACode',
  'verifyWithDiiaBarcode',
  'revokeDiiaVerification',
])

// The signed-in user is refetched on every window focus: a failing /user/me
// is reported once until it succeeds again, not on each focus.
let userFailureReported = false

const endpointOf = (action: AnyAction): string | undefined => action.meta?.arg?.endpointName

const isUnauthorized = (payload: any) => {
  const data = payload?.data
  return (
    payload?.status === 401 ||
    payload?.status === '401' ||
    data?.code === 'NOT_AUTHENTICATED' ||
    data?.message === 'Unauthorized' ||
    data?.error === 'Unauthorized' ||
    (typeof data?.message === 'string' && data.message.startsWith('Authentication required'))
  )
}

export const toastMiddleware: Middleware = () => (next) => (action: unknown) => {
  const typedAction = action as AnyAction
  const endpoint = endpointOf(typedAction)

  if (isRejectedWithValue(typedAction)) {
    const payload = typedAction.payload as any
    if (endpoint && (QUIET_QUERIES.has(endpoint) || OWN_ERROR_UI.has(endpoint)))
      return next(typedAction)
    // 401 is handled by the login wall and the login form.
    if (isUnauthorized(payload)) return next(typedAction)
    // Login failures are rendered inline by the login form.
    if (
      payload?.data?.code === 'INVALID_CREDENTIALS' ||
      payload?.data?.code === 'ACCOUNT_NOT_VERIFIED'
    ) {
      return next(typedAction)
    }
    if (endpoint === 'getUser') {
      if (userFailureReported) return next(typedAction)
      userFailureReported = true
    }
    // Network failures surface as raw "TypeError: Failed to fetch" otherwise
    // (e.g. while the backend restarts during a deploy).
    const message =
      payload?.status === 'FETCH_ERROR' || payload?.status === 'TIMEOUT_ERROR'
        ? String(i18n.t('errors.NETWORK'))
        : errorText(payload)
    // One toast per distinct message: a debounced autocomplete or a poller
    // failing on every tick used to stack the same error a dozen times.
    toast.error(message, { toastId: `error:${message}` })
    return next(typedAction)
  }

  if (typedAction.type?.endsWith('/fulfilled')) {
    if (endpoint === 'getUser') userFailureReported = false
    if (typedAction.meta?.arg?.type === 'mutation' && !(endpoint && OWN_SUCCESS_UI.has(endpoint))) {
      const response = typedAction.payload as any
      if (response?.success !== false && isReadableServerText(response?.message)) {
        toast.success(response.message)
      }
    }
  }

  return next(typedAction)
}
